import React from 'react';
import type { DataflowExecutiveRibbonData } from '../../services/dataflowExecutiveDiagnostics';
import { shouldShowCount } from '../../services/tplSeverityStyles';
import { TPL_SEVERITY_STYLE } from '../../services/tplSeverityStyles';

interface DataflowExecutiveRibbonProps {
  data: DataflowExecutiveRibbonData;
  compact?: boolean;
  className?: string;
}

/**
 * Barra executiva global DataFlow — IEL · Maturidade · Riscos · Perdas · Ações · Status.
 */
export const DataflowExecutiveRibbon: React.FC<DataflowExecutiveRibbonProps> = ({
  data,
  compact = false,
  className = '',
}) => {
  const statusStyle = TPL_SEVERITY_STYLE[data.status.tone];

  const metrics: Array<{ key: string; label: string; value: string | number; show: boolean }> = [
    { key: 'iel', label: 'IEL', value: data.iel, show: true },
    { key: 'mat', label: 'Maturidade', value: data.maturidade, show: true },
    { key: 'risc', label: 'Riscos', value: data.riscos, show: shouldShowCount(data.riscos) },
    { key: 'perd', label: 'Perdas', value: data.perdas, show: shouldShowCount(data.perdas) },
    { key: 'acoes', label: 'Ações', value: data.acoes, show: shouldShowCount(data.acoes) },
  ];

  const visible = metrics.filter((m) => m.show);

  return (
    <div
      className={`rounded-lg border border-slate-200 bg-slate-50/80 dark:border-slate-800 dark:bg-slate-900/40 ${compact ? 'px-2 py-1.5' : 'px-3 py-2'} ${className}`}
    >
      <div className={`flex flex-wrap items-center gap-x-4 gap-y-1 ${compact ? 'text-[10px]' : 'text-[11px]'}`}>
        {visible.map((m) => (
          <MetricDot key={m.key} label={m.label} value={m.value} />
        ))}
        <div className="ml-auto flex items-center gap-2">
          <span className="text-[9px] font-bold uppercase tracking-wide text-slate-400">Status</span>
          <span className={`rounded px-2 py-0.5 text-[9px] font-bold uppercase ${statusStyle.badge}`}>
            {data.status.label}
          </span>
        </div>
      </div>

      {data.status.reasons.length > 0 && (
        <div className={`mt-1.5 border-t border-slate-200/80 pt-1.5 dark:border-slate-700/80 ${compact ? 'text-[9px]' : 'text-[10px]'}`}>
          <p className="mb-0.5 font-bold uppercase tracking-wide text-slate-400">Motivos</p>
          <ul className={`space-y-0.5 ${statusStyle.text}`}>
            {data.status.reasons.map((r) => (
              <li key={r}>• {r}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

function MetricDot({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="flex items-baseline gap-1.5">
      <span className="font-bold uppercase tracking-wide text-slate-500">{label}</span>
      <span className="font-black tabular-nums text-slate-800 dark:text-slate-100">{value}</span>
    </div>
  );
}

export default DataflowExecutiveRibbon;
